import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import {
  Download,
  RefreshCw,
  FileText,
  Calendar,
  User,
  Filter,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import reportsService from "@/services/reportsService";

/**
 * Report History Component
 *
 * Displays list of previously generated reports with download capability.
 *
 * Requirements: 18.1-18.9
 */
const ReportHistory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [filters, setFilters] = useState({
    report_type: "",
    date_from: "",
    date_to: "",
    user_id: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.report_type) params.report_type = filters.report_type;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.user_id) params.user_id = filters.user_id;

      const response = await reportsService.getHistory(params);

      if (response?.data?.success) {
        setHistory(response.data.data.history || []);
      }
    } catch (error) {
      console.error("Failed to load report history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    loadHistory();
  };

  const handleClearFilters = () => {
    setFilters({
      report_type: "",
      date_from: "",
      date_to: "",
      user_id: "",
    });
  };

  const handleDownload = async (historyId) => {
    try {
      const response = await reportsService.downloadExport(historyId);

      // Get filename from history record
      const historyRecord = history.find((h) => h.id === historyId);
      const extension = historyRecord?.export_format === "pdf" ? "pdf" : "xlsx";
      const filename = `report_${historyId}_${Date.now()}.${extension}`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-NG");
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const getReportTypeLabel = (type) => {
    const labels = {
      account_ledger: "Account Ledger",
      budget_report: "Budget Report",
      transaction_list: "Transaction List",
      profit_loss: "Profit & Loss",
      balance_sheet: "Balance Sheet",
      cash_flow: "Cash Flow",
    };
    return labels[type] || type;
  };

  const getFormatBadge = (format) => {
    const colors = {
      pdf: "bg-red-100 text-red-800",
      excel: "bg-green-100 text-green-800",
      none: "bg-gray-100 text-gray-800",
    };
    return (
      <Badge className={colors[format] || colors.none}>
        {format.toUpperCase()}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Report History</h1>
          <p className="text-gray-600 mt-1">
            View and download previously generated reports
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
          <Button onClick={() => navigate("/finance/reports-center")}>
            Back to Reports
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Report Type</Label>
                <Select
                  value={filters.report_type}
                  onValueChange={(value) =>
                    setFilters({ ...filters, report_type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All types</SelectItem>
                    <SelectItem value="account_ledger">
                      Account Ledger
                    </SelectItem>
                    <SelectItem value="budget_report">Budget Report</SelectItem>
                    <SelectItem value="transaction_list">
                      Transaction List
                    </SelectItem>
                    <SelectItem value="profit_loss">Profit & Loss</SelectItem>
                    <SelectItem value="balance_sheet">Balance Sheet</SelectItem>
                    <SelectItem value="cash_flow">Cash Flow</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) =>
                    setFilters({ ...filters, date_from: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) =>
                    setFilters({ ...filters, date_to: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>&nbsp;</Label>
                <div className="flex gap-2">
                  <Button onClick={handleApplyFilters} className="flex-1">
                    Apply
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleClearFilters}
                    className="flex-1"
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Retention Notice */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">
                Report Retention Policy
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Reports are automatically retained for 90 days. After this
                period, they will be archived and may require special access to
                retrieve.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* History Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Report History</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={loadHistory}
              disabled={loading}
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Loading history...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p>
                No reports found. Generate your first report to get started.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Report Type</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead>Generated By</TableHead>
                    <TableHead>Format</TableHead>
                    <TableHead>Records</TableHead>
                    <TableHead>File Size</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((record) => (
                    <TableRow key={record.id} className="hover:bg-gray-50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-400" />
                          <div>
                            <p className="font-medium">
                              {record.Configuration?.name ||
                                getReportTypeLabel(record.report_type)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {getReportTypeLabel(record.report_type)}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">
                            {formatDate(record.created_at)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">
                            {record.User?.name || "Unknown"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getFormatBadge(record.export_format)}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">
                          {record.record_count?.toLocaleString() || "N/A"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">
                          {formatFileSize(record.file_size)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {record.export_format !== "none" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(record.id)}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportHistory;
